import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";

const formatMoney = (value) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number.isFinite(Number(value)) ? Number(value) : 0);

const CashPaymentDialog = ({ open, amountDue, isProcessing, onClose, onRecordCash, t }) => {
  const [cashReceived, setCashReceived] = useState("");
  const [submitError, setSubmitError] = useState("");
  const inputRef = useRef(null);

  const receivedAmount = useMemo(() => {
    const parsed = Number(cashReceived);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  }, [cashReceived]);

  const balanceOwed = Math.max(Number(amountDue || 0) - receivedAmount, 0);
  const hasCashValue = cashReceived.trim() !== "" && receivedAmount > 0;

  useEffect(() => {
    if (!open) return;
    setCashReceived(Number(amountDue || 0) > 0 ? Number(amountDue || 0).toFixed(2) : "");
    setSubmitError("");
    window.setTimeout(() => {
      if (inputRef.current) inputRef.current.select();
    }, 50);
  }, [amountDue, open]);

  const handleClose = () => {
    if (isProcessing) return;
    onClose();
  };

  const handleCashReceivedChange = (value) => {
    if (value === "") {
      setCashReceived("");
      setSubmitError("");
      return;
    }

    const nextAmount = Number(value);
    if (!Number.isFinite(nextAmount)) {
      setCashReceived(value);
      return;
    }

    if (nextAmount > Number(amountDue || 0)) {
      setCashReceived(Number(amountDue || 0).toFixed(2));
      setSubmitError(t("Console.ActionPopup.CashReceivedCannotExceedDue", { defaultValue: "Cash received cannot be greater than the amount due." }));
      return;
    }

    setCashReceived(value);
    setSubmitError("");
  };

  const handleRecordCash = async () => {
    if (!hasCashValue || isProcessing) return;

    if (receivedAmount > Number(amountDue || 0)) {
      setSubmitError(t("Console.ActionPopup.CashReceivedCannotExceedDue", { defaultValue: "Cash received cannot be greater than the amount due." }));
      return;
    }

    setSubmitError("");
    try {
      const shouldClose = await onRecordCash(Number(receivedAmount.toFixed(2)));
      if (shouldClose !== false) {
        onClose();
      }
    } catch (error) {
      setSubmitError(error && error.message ? error.message : "Failed to record cash payment.");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2, overflow: "hidden" } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          p: { xs: 2, sm: 2.5 },
          color: "#ffffff",
          background: "linear-gradient(90deg, #059669 0%, #0d9488 100%)",
          borderBottom: "1px solid #6ee7b7",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 36,
              height: 36,
              borderRadius: 1.5,
              backgroundColor: "rgba(255,255,255,0.2)",
            }}
          >
            <AttachMoneyIcon />
          </Box>
          <Typography sx={{ fontSize: { xs: "1.1rem", sm: "1.25rem" }, fontWeight: 800 }}>
            {t("Console.ActionPopup.CashPayment", { defaultValue: "Cash Payment" })}
          </Typography>
        </Box>
        <IconButton
          onClick={handleClose}
          disabled={isProcessing}
          aria-label="Close"
          sx={{ color: "#ffffff", "&:hover": { backgroundColor: "rgba(255,255,255,0.16)" } }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: { xs: 2, sm: 3 }, backgroundColor: "#ffffff" }}>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5, mb: 2 }}>
          <Box sx={{ border: "1px solid #e5e7eb", backgroundColor: "#f9fafb", borderRadius: 2, p: 1.5 }}>
            <Typography sx={{ fontSize: "0.65rem", fontWeight: 800, textTransform: "uppercase", color: "#6b7280" }}>
              {t("Console.ActionPopup.AmountDue", { defaultValue: "Amount Due" })}
            </Typography>
            <Typography sx={{ mt: 0.5, fontSize: "1.35rem", fontWeight: 800, color: "#111827" }}>
              {formatMoney(amountDue)}
            </Typography>
          </Box>
          <Box
            sx={{
              border: balanceOwed > 0 ? "1px solid #fcd34d" : "1px solid #6ee7b7",
              backgroundColor: balanceOwed > 0 ? "#fffbeb" : "#ecfdf5",
              borderRadius: 2,
              p: 1.5,
            }}
          >
            <Typography sx={{ fontSize: "0.65rem", fontWeight: 800, textTransform: "uppercase", color: "#6b7280" }}>
              {t("Console.ActionPopup.BalanceOwed", { defaultValue: "Balance Owed" })}
            </Typography>
            <Typography sx={{ mt: 0.5, fontSize: "1.35rem", fontWeight: 800, color: balanceOwed > 0 ? "#b45309" : "#047857" }}>
              {formatMoney(balanceOwed)}
            </Typography>
          </Box>
        </Box>

        <Typography sx={{ mb: 1, fontSize: "0.9rem", fontWeight: 700, color: "#111827" }}>
          {t("Console.ActionPopup.CashReceived", { defaultValue: "Cash Received" })}
        </Typography>
        <TextField
          inputRef={inputRef}
          fullWidth
          type="number"
          inputMode="decimal"
          value={cashReceived}
          onChange={(event) => handleCashReceivedChange(event.target.value)}
          placeholder="0.00"
          InputProps={{
            startAdornment: <Typography sx={{ mr: 1, color: "#6b7280", fontWeight: 700 }}>$</Typography>,
            inputProps: { min: 0, max: Number(amountDue || 0), step: "0.01" },
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
              fontSize: "1.1rem",
              fontWeight: 700,
              "&.Mui-focused fieldset": { borderColor: "#10b981", borderWidth: 2 },
            },
          }}
        />

        <Box sx={{ mt: 2, border: "1px solid #e5e7eb", borderRadius: 2, p: 1.5 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
            <Typography sx={{ color: "#4b5563", fontSize: "0.9rem", fontWeight: 600 }}>
              {t("Console.ActionPopup.CashReceived", { defaultValue: "Cash Received" })}
            </Typography>
            <Typography sx={{ color: "#111827", fontSize: "0.9rem", fontWeight: 800 }}>
              {formatMoney(receivedAmount)}
            </Typography>
          </Box>
          <Box sx={{ mt: 1, display: "flex", justifyContent: "space-between", gap: 2 }}>
            <Typography sx={{ color: "#4b5563", fontSize: "0.9rem", fontWeight: 600 }}>
              {t("Console.ActionPopup.TotalMinusCash", { defaultValue: "Total Minus Cash" })}
            </Typography>
            <Typography sx={{ color: "#111827", fontSize: "0.9rem", fontWeight: 800 }}>
              {formatMoney(balanceOwed)}
            </Typography>
          </Box>
        </Box>

        {submitError ? (
          <Typography sx={{ mt: 1.5, color: "#dc2626", fontSize: "0.8rem", fontWeight: 600 }}>
            {submitError}
          </Typography>
        ) : null}

        {!hasCashValue ? (
          <Typography sx={{ mt: 1.5, color: "#b45309", fontSize: "0.8rem", fontWeight: 600 }}>
            {t("Console.ActionPopup.CashPreviewHelp", { defaultValue: "Enter the cash amount received to preview the remaining balance." })}
          </Typography>
        ) : null}
      </DialogContent>

      <DialogActions sx={{ p: { xs: 2, sm: 3 }, pt: 2, borderTop: "1px solid #e5e7eb", gap: 1.5 }}>
        <Button
          onClick={handleClose}
          disabled={isProcessing}
          variant="outlined"
          sx={{ minWidth: 100, textTransform: "none", borderWidth: 2, fontWeight: 700 }}
        >
          {t("Console.ActionPopup.Cancel", { defaultValue: "Cancel" })}
        </Button>
        <Button
          onClick={handleRecordCash}
          disabled={!hasCashValue || isProcessing}
          variant="contained"
          sx={{ minWidth: 120, textTransform: "none", backgroundColor: "#059669", fontWeight: 700, "&:hover": { backgroundColor: "#047857" } }}
        >
          {isProcessing ? <CircularProgress size={18} color="inherit" /> : t("Console.ActionPopup.RecordCash", { defaultValue: "Record Cash" })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

CashPaymentDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  amountDue: PropTypes.number,
  isProcessing: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onRecordCash: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

CashPaymentDialog.defaultProps = { amountDue: 0, isProcessing: false };

export default CashPaymentDialog;



// WEBPACK FOOTER //
// ./src/components/assign-prescriptions/counter/dialogs/CashPaymentDialog.jsx